/**
 * Theme chips. The keys are stable (used by ad landing links
 * `/try?interest=<key>`, docs/ADS-LIEBLINGSTHEMEN-2026-09.md, and stored as
 * `stories.theme_tag`); the labels come from i18n `interests.items.<key>` and
 * go into the ideas request as plain text. Nothing is stored on the user.
 * Ordered by measured German search demand (Keyword Planner, 2026-08-28).
 */
export const INTEREST_KEYS = [
  'princess', 'unicorn', 'firefighter', 'dinosaur', 'dragon', 'pirate', 'police', 'animals',
  'horses', 'space', 'digger', 'tractor', 'mermaid', 'football', 'knight', 'robot',
] as const;

export type InterestKey = (typeof INTEREST_KEYS)[number];

/**
 * The chips shown by default in the create picker (Kai, 2026-08-29: "the most
 * desired ones") — the six with the highest measured search demand; the rest
 * sit in the "More" dropdown next to a free-text option.
 */
export const PRIMARY_INTEREST_KEYS: readonly InterestKey[] = [
  'princess', 'unicorn', 'firefighter', 'dinosaur', 'dragon', 'pirate',
];

/** One emoji per theme — the chips are used by (and with) children, so they read visually. */
export const INTEREST_EMOJI: Record<InterestKey, string> = {
  princess: '👸',
  unicorn: '🦄',
  firefighter: '🚒',
  dinosaur: '🦖',
  dragon: '🐉',
  pirate: '🏴‍☠️',
  police: '🚓',
  animals: '🐾',
  horses: '🐴',
  space: '🚀',
  digger: '🏗️',
  tractor: '🚜',
  mermaid: '🧜‍♀️',
  football: '⚽',
  knight: '⚔️',
  robot: '🤖',
};

export function isInterestKey(value: string | null | undefined): value is InterestKey {
  return !!value && (INTEREST_KEYS as readonly string[]).includes(value);
}

export function interestEmoji(key: string): string {
  return isInterestKey(key) ? INTEREST_EMOJI[key] : '';
}

/**
 * Seeded library stories carry catalog tags in `stories.theme` ("adventure",
 * "first day of school", …) rather than interest keys. The reader's closing
 * slides use this map to turn them into a localized topic (i18n key).
 */
export const CATALOG_THEME_LABEL_KEYS: Record<string, string> = {
  adventure: 'reader.themeAdventure',
  fantasy: 'reader.themeFantasy',
  nature: 'reader.themeNature',
  community: 'reader.themeCommunity',
  family: 'reader.themeFamily',
  'first day of school': 'reader.themeSchool',
};
